package com.schoolconnect.app.datasource

import com.russhwolf.settings.Settings

/**
 * Persists user-interface navigation preferences independently from the
 * authentication/session records. The context is part of the key so a user
 * can safely switch between roles or schools without inheriting another
 * context's last rail item.
 */
internal object NavigationStateStore {
    private const val LAST_RAIL_PREFIX = "${APP_STORAGE_PREFIX}last_rail_"
    private const val SIDEBAR_COLLAPSED_PREFIX = "${APP_STORAGE_PREFIX}sidebar_collapsed_"
    private const val EXPANDED_RAIL_GROUP_PREFIX = "${APP_STORAGE_PREFIX}expanded_rail_group_"

    private val settingsOrNull: Settings? by lazy(LazyThreadSafetyMode.SYNCHRONIZED) {
        runCatching { Settings() }.getOrNull()
    }

    fun getLastRailId(userId: String, roleCode: String, schoolId: String): String {
        return settingsOrNull?.getString(
            lastRailKey(userId, roleCode, schoolId),
            ""
        ).orEmpty().trim()
    }

    fun setLastRailId(userId: String, roleCode: String, schoolId: String, railId: String) {
        val normalizedRailId = railId.trim()
        val key = lastRailKey(userId, roleCode, schoolId)
        if (normalizedRailId.isBlank()) {
            settingsOrNull?.remove(key)
        } else {
            settingsOrNull?.putString(key, normalizedRailId)
        }
    }

    fun clearLastRailId(userId: String, roleCode: String, schoolId: String) {
        settingsOrNull?.remove(lastRailKey(userId, roleCode, schoolId))
    }

    fun getSidebarCollapsed(userId: String, roleCode: String, schoolId: String): Boolean {
        return settingsOrNull?.getBoolean(
            sidebarCollapsedKey(userId, roleCode, schoolId),
            false
        ) ?: false
    }

    fun setSidebarCollapsed(userId: String, roleCode: String, schoolId: String, collapsed: Boolean) {
        settingsOrNull?.putBoolean(
            sidebarCollapsedKey(userId, roleCode, schoolId),
            collapsed
        )
    }

    fun getExpandedRailGroup(roleCode: String, schoolId: String): String {
        return settingsOrNull?.getString(
            expandedRailGroupKey(roleCode, schoolId),
            ""
        ).orEmpty().trim()
    }

    fun setExpandedRailGroup(roleCode: String, schoolId: String, groupKey: String) {
        val normalizedGroupKey = groupKey.trim()
        val key = expandedRailGroupKey(roleCode, schoolId)
        if (normalizedGroupKey.isBlank()) {
            settingsOrNull?.remove(key)
        } else {
            settingsOrNull?.putString(key, normalizedGroupKey)
        }
    }

    internal fun lastRailKey(userId: String, roleCode: String, schoolId: String): String {
        return compactContextKey(LAST_RAIL_PREFIX, userId, roleCode, schoolId)
    }

    internal fun sidebarCollapsedKey(userId: String, roleCode: String, schoolId: String): String {
        return compactContextKey(SIDEBAR_COLLAPSED_PREFIX, userId, roleCode, schoolId)
    }

    internal fun expandedRailGroupKey(roleCode: String, schoolId: String): String {
        return compactContextKey(EXPANDED_RAIL_GROUP_PREFIX, "", roleCode, schoolId)
    }

    /**
     * java.util.prefs limits preference key length on desktop. IDs from the
     * backend are UUIDs, so concatenating all context parts can exceed that
     * limit. A stable 64-bit hash keeps the key bounded without dropping any
     * of the user/role/school scope from the identity of the entry.
     */
    private fun compactContextKey(prefix: String, userId: String, roleCode: String, schoolId: String): String {
        val context = listOf(userId, roleCode, schoolId)
            .joinToString("\u0000") { it.trim().ifBlank { "global" } }
        var hash = -0x340d631b7bdddcdbL
        context.forEach { character ->
            hash = hash xor character.code.toLong()
            hash *= 0x100000001b3L
        }
        return prefix + hash.toString(16)
    }
}
